import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

import { api, formatApiError } from "@/lib/api";
import { FolderIcon, folderColorClasses } from "@/components/folderTheme";

/**
 * Toolbar that appears at the bottom of a list page when ≥1 row is
 * selected.  Lets the user *add* every selected target to a chosen
 * folder via the existing endpoints (`/folders/{id}/items` for runs,
 * `/folders/{id}/workflow-items` for workflows).
 *
 * Pass `targetType="run" | "workflow"` (default "run") to switch the
 * endpoint and on-screen labels.
 *
 * "Move" semantics (i.e. remove-from-old then add-to-new in one shot)
 * are NOT implemented as a single API call here on purpose: the user's
 * own brief says do it as two API calls.  Removing from a specific
 * source folder is left to the per-row chip popover where the user can
 * see *which* folders the target already belongs to.  This toolbar's
 * job is to make adding N targets to one folder in a single click
 * trivial.
 */
export default function BulkFolderMoveBar({
  selectedIds,
  onClear,
  targetType = "run",
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // R3-Phase-1 — strict "Move (replace memberships)" mode.  Off by
  // default; opt-in via the pill toggle at the top of the popover.
  const [mode, setMode] = useState("add"); // "add" | "move"

  const folders = useQuery({
    queryKey: ["folders", "all"],
    queryFn: async () =>
      (await api.get("/folders?visibility=all&page_size=200")).data,
    enabled: open,
  });

  const addAll = useMutation({
    mutationFn: async ({ folderId }) => {
      const ids = Array.from(selectedIds);
      if (mode === "move") {
        // One atomic backend call per item-kind that replaces
        // memberships in one go and emits the
        // `folder_workflow_item_moved` / `folder_item_moved`
        // audit row server-side.
        const url =
          targetType === "workflow"
            ? `/folders/${folderId}/move-workflows`
            : `/folders/${folderId}/move-runs`;
        const body =
          targetType === "workflow"
            ? { workflow_ids: ids }
            : { run_ids: ids };
        const res = (await api.post(url, body)).data;
        // Surface a single rollup: counts of items that actually
        // changed folders vs. items that were already in target.
        const moved = Object.values(res.results || {}).filter(
          (r) => r.from_folder_ids?.length || !r.already_in_target,
        ).length;
        return { ok: moved, fail: ids.length - moved, ids: ids.length };
      }
      // Default "add" mode — parallel idempotent upserts.
      const results = await Promise.allSettled(
        ids.map((id) =>
          targetType === "workflow"
            ? api.post(`/folders/${folderId}/workflow-items`, { workflow_id: id })
            : api.post(`/folders/${folderId}/items`, { run_id: id }),
        ),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail, ids: ids.length };
    },
    onSuccess: ({ ok, fail, ids }, { folderName }) => {
      const noun =
        targetType === "workflow"
          ? ids === 1 ? "workflow" : "workflows"
          : ids === 1 ? "run" : "runs";
      const verbed = mode === "move" ? "Moved" : "Added";
      if (fail === 0) {
        toast.success(`${verbed} ${ok} ${noun} to "${folderName}"`);
      } else {
        toast.warning(`${verbed} ${ok}/${ok + fail} ${noun} to "${folderName}" — ${fail} failed.`);
      }
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      queryClient.invalidateQueries({ queryKey: ["folder-summary"] });
      queryClient.invalidateQueries({ queryKey: ["folder-summary-by-workflow"] });
      onClear?.();
      setOpen(false);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const items = useMemo(() => folders.data?.items || [], [folders.data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((f) => f.name.toLowerCase().includes(q));
  }, [items, search]);

  const count = selectedIds.size;
  if (count === 0) return null;

  return (
    <div
      data-testid="bulk-folder-bar"
      className="sticky bottom-4 z-30 mx-auto flex max-w-3xl items-center gap-3 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 shadow-lg shadow-indigo-900/5"
    >
      <span
        className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700"
        data-testid="bulk-folder-bar-count"
      >
        {count} selected
      </span>
      <span className="text-xs text-slate-500">on this page</span>
      <div className="ml-auto flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              data-testid="bulk-folder-move-button"
              className="h-8 gap-1.5 bg-indigo-600 text-xs hover:bg-indigo-700"
            >
              <ArrowRightLeft size={12} />
              Move to folder
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-72 p-0"
            data-testid="bulk-folder-popover"
          >
            <div className="border-b border-slate-200 p-2 space-y-2">
              <div
                className="grid grid-cols-2 overflow-hidden rounded-md border border-slate-200 text-[11px] font-medium"
                role="tablist"
                aria-label="Folder action mode"
                data-testid="bulk-folder-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => setMode("add")}
                  className={[
                    "px-2 py-1.5",
                    mode === "add"
                      ? "bg-indigo-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50",
                  ].join(" ")}
                  data-testid="bulk-folder-mode-add"
                >
                  Add to folder
                </button>
                <button
                  type="button"
                  onClick={() => setMode("move")}
                  className={[
                    "px-2 py-1.5",
                    mode === "move"
                      ? "bg-indigo-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50",
                  ].join(" ")}
                  data-testid="bulk-folder-mode-move"
                >
                  Move (replace)
                </button>
              </div>
              {mode === "move" && (
                <p
                  className="rounded bg-indigo-50 px-2 py-1 text-[10px] leading-tight text-indigo-800"
                  data-testid="bulk-folder-move-hint"
                >
                  Selected items will be removed from any other folders
                  you can modify and placed only in the chosen one.
                </p>
              )}
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search folders…"
                className="h-8 text-xs"
                data-testid="bulk-folder-search"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {folders.isLoading ? (
                <div className="flex items-center justify-center py-4 text-xs text-slate-500">
                  <Loader2 size={12} className="mr-1.5 animate-spin" /> Loading…
                </div>
              ) : filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-slate-500">
                  {search ? "No matches." : "No folders yet."}
                </p>
              ) : (
                filtered.map((f) => {
                  const palette = folderColorClasses(f.color);
                  const canModify = f.can_modify_items;
                  return (
                    <button
                      type="button"
                      key={f.folder_id}
                      disabled={!canModify || addAll.isPending}
                      onClick={() =>
                        addAll.mutate({ folderId: f.folder_id, folderName: f.name })
                      }
                      className={[
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs",
                        canModify ? "hover:bg-slate-50" : "cursor-not-allowed opacity-60",
                      ].join(" ")}
                      data-testid={`bulk-folder-row-${f.folder_id}`}
                    >
                      <span
                        className={[
                          "grid h-6 w-6 place-items-center rounded",
                          palette.bg,
                          palette.text,
                        ].join(" ")}
                      >
                        <FolderIcon name={f.icon} size={12} />
                      </span>
                      <span className="flex-1 truncate font-medium text-slate-800">
                        {f.name}
                      </span>
                      {f.visibility === "org" && (
                        <span className="rounded bg-slate-100 px-1 text-[9px] uppercase text-slate-500">
                          Org
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClear}
          data-testid="bulk-folder-clear"
          className="h-8 gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <X size={12} />
          Clear
        </Button>
      </div>
    </div>
  );
}
